/**
 * Lightweight client telemetry for webfont loading.
 *
 * Records:
 *   - time-to-fonts-ready (document.fonts.ready duration since navigationStart)
 *   - per-family load time for Geist and Geist Mono
 *   - whether each family actually resolved (vs. still using metric-matched fallback)
 *   - whether the non-blocking preload fallback path was used
 *     (i.e. the stylesheet arrived via <link media="print" onload=...>
 *      swap-to-all pattern, or a JS-injected <link> after load)
 *   - a coarse "flash risk" flag if fonts took > 1s to become ready
 *
 * Zero deps. Emits a single `font_load` GA4 event via the existing
 * analytics helper and mirrors to `window.__fontTelemetry` for debugging.
 */

import { trackEvent } from "./analytics";

type FontSample = {
  family: string;
  weight: string;
  size: string;
};

const SAMPLES: FontSample[] = [
  { family: "Geist", weight: "600", size: "24px" },
  { family: "Geist", weight: "400", size: "16px" },
  { family: "Geist Mono", weight: "400", size: "14px" },
];

const TELEMETRY_KEY = "__fontTelemetry" as const;

declare global {
  interface Window {
    [TELEMETRY_KEY]?: FontTelemetryPayload;
  }
}

export type FontTelemetryPayload = {
  ready_ms: number;
  families: Record<string, { loaded: boolean; ms: number | null }>;
  preload_fallback_used: boolean;
  strategy: "blocking-stylesheet" | "preload-swap" | "js-injected" | "unknown";
  flash_risk: boolean;
  ua: string;
};

/**
 * Detect how the Google Fonts stylesheet was loaded.
 *
 * - blocking-stylesheet: plain <link rel="stylesheet"> in head
 * - preload-swap:        <link rel="preload" as="style" onload="this.rel='stylesheet'">
 * - js-injected:         appended to <head> after DOMContentLoaded
 * - unknown:             couldn't find a matching link
 */
function detectStrategy(): FontTelemetryPayload["strategy"] {
  const links = Array.from(
    document.querySelectorAll<HTMLLinkElement>('link[href*="fonts.googleapis.com"]'),
  );
  if (links.length === 0) return "unknown";

  // If we ever set rel="preload" as style + swap, the DOM shows rel="stylesheet"
  // by load time — so we tag the element up front. See main.tsx wiring.
  const marked = links.find((l) => l.dataset.fontStrategy);
  if (marked?.dataset.fontStrategy) {
    return marked.dataset.fontStrategy as FontTelemetryPayload["strategy"];
  }

  return "blocking-stylesheet";
}

/**
 * Record when the non-blocking preload fallback was activated.
 * Call this from the <link>'s onload handler (or the JS injector) so we
 * know the swap actually happened rather than silently failing to a
 * synchronous stylesheet.
 */
export function markPreloadFallbackUsed(
  strategy: Exclude<FontTelemetryPayload["strategy"], "unknown"> = "preload-swap",
) {
  const link = document.querySelector<HTMLLinkElement>(
    'link[href*="fonts.googleapis.com"][rel="stylesheet"]',
  );
  if (link) link.dataset.fontStrategy = strategy;
  (window as unknown as Record<string, unknown>).__fontPreloadFallback = true;
}

function nowMs(): number {
  return performance.now();
}

async function measureFamily(sample: FontSample): Promise<{ loaded: boolean; ms: number | null }> {
  const spec = `${sample.weight} ${sample.size} "${sample.family}"`;
  const start = nowMs();
  try {
    // fonts.load resolves when the specific face is available (or immediately if cached).
    await (document as Document & { fonts: FontFaceSet }).fonts.load(spec);
    const loaded = (document as Document & { fonts: FontFaceSet }).fonts.check(spec);
    return { loaded, ms: loaded ? Math.round(nowMs() - start) : null };
  } catch {
    return { loaded: false, ms: null };
  }
}

export async function collectFontTelemetry(): Promise<FontTelemetryPayload | null> {
  if (typeof document === "undefined" || !("fonts" in document)) return null;
  const fontSet = (document as Document & { fonts: FontFaceSet }).fonts;

  const readyStart = nowMs();
  try {
    await fontSet.ready;
  } catch {
    /* ignore */
  }
  const ready_ms = Math.round(nowMs() - readyStart + (performance.timeOrigin ? 0 : 0));

  const perFamily = await Promise.all(SAMPLES.map(measureFamily));
  const families: FontTelemetryPayload["families"] = {};
  SAMPLES.forEach((s, i) => {
    families[s.family] = perFamily[i];
  });

  const preload_fallback_used = Boolean(
    (window as unknown as Record<string, unknown>).__fontPreloadFallback,
  );

  const payload: FontTelemetryPayload = {
    ready_ms,
    families,
    preload_fallback_used,
    strategy: detectStrategy(),
    flash_risk: ready_ms > 1000,
    ua: navigator.userAgent.slice(0, 120),
  };

  window[TELEMETRY_KEY] = payload;

  // GA4 event — flatten nested objects for parameter compatibility.
  trackEvent("font_load", {
    ready_ms,
    strategy: payload.strategy,
    preload_fallback_used,
    flash_risk: payload.flash_risk,
    heading_ms: families["Geist"]?.ms ?? -1,
    heading_loaded: families["Geist"]?.loaded ?? false,
    body_ms: families["Geist"]?.ms ?? -1,
    body_loaded: families["Geist"]?.loaded ?? false,
    mono_ms: families["Geist Mono"]?.ms ?? -1,
    mono_loaded: families["Geist Mono"]?.loaded ?? false,
  });

  return payload;
}

/**
 * Entry point: run once per session, after the page has settled so we
 * don't compete with critical rendering work.
 */
export function initFontTelemetry() {
  if (typeof window === "undefined") return;
  if ((window as unknown as Record<string, unknown>).__fontTelemetryInit) return;
  (window as unknown as Record<string, unknown>).__fontTelemetryInit = true;

  const kick = () => {
    // Defer past first paint / hydration; requestIdleCallback where available.
    const runner = () => {
      void collectFontTelemetry();
    };
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    if (typeof w.requestIdleCallback === "function") {
      w.requestIdleCallback(runner, { timeout: 3000 });
    } else {
      setTimeout(runner, 1500);
    }
  };

  if (document.readyState === "complete") kick();
  else window.addEventListener("load", kick, { once: true });
}
